// pressure - P psia
// temp - T fahrenheit
// user entered pb

import { Water_Volume_Factor_Options_Enum } from "gql/api";
import { PercentageUnits } from "@/libs/units/percentage";

// well_test_water_rate_stb_day input
// oil rate Q_o stbd
// water cut - WC %
// returns Q_w stbd
export const getWaterFlowRateSTDB = (
  oilFlowSTDB: number,
  waterCutRatio: number
): number => {
  return oilFlowSTDB * waterCutRatio;
};

// press - P psia
// temp - T fahrenheit
// gor - GOR scf/stb
// gas solubility in oil - R_so scf/stb
// returns R_sw_corr scf/stb
export function water_phase_gas_solubility_corr(
  pressure: number,
  tempf: number,
  GOR: number,
  R_so: number
): number {
  const a = 8.15839 - 0.0612265 * tempf + 0.000191663 * (tempf * tempf);
  const b =
    0.0101021 -
    0.0000744241 * tempf +
    0.000000305553 * tempf * tempf -
    0.000000000294883 * tempf * tempf * tempf;
  const c =
    (-9.02505 +
      0.130237 * tempf -
      (8.53425 * tempf * tempf) / 10000 +
      (2.34122 * tempf * tempf * tempf) / 1000000 -
      (2.37049 * tempf * tempf * tempf * tempf) / 1000000000) /
    10000000;

  const rsw = a + b * pressure + c * pressure * pressure;
  const min_check = GOR - R_so;
  if (rsw > min_check) {
    return min_check;
  } else {
    return rsw;
  }
}

// pressure - P psia
// temp - T fahrenheit
// returns water phase iso compress psi^-1
export function water_phase_isothermal_compressibility(
  pressure: number,
  tempf: number
): number {
  return (
    (3.855 -
      0.000134 * pressure +
      (-0.01052 + 0.000000477 * pressure) * tempf +
      (0.000039267 - 0.00000000088 * pressure) * tempf * tempf) /
    1000000
  );
}

// pressure - P psia
// temp - T fahrenheit
// returns B_wb bbl/stb
export function water_phase_volume_factor_at_or_below_bubble_point(
  pressure: number,
  tempf: number
): number {
  const tm60 = tempf - 60;
  return (
    1 + 0.00012 * tm60 + 0.000001 * tm60 * tm60 - (3.33 * pressure) / 1000000
  );
}

// pressure - P psia
// temp - T fahrenheit
// bubble point press - P_b psia
// returns B_wa bbl/stb
export function water_phase_volume_factor_above_bubble_point(
  pressure: number,
  tempf: number,
  bubble_point_pressure: number
): number {
  const c_w = water_phase_isothermal_compressibility(pressure, tempf);
  const b_wb = water_phase_volume_factor_at_or_below_bubble_point(
    bubble_point_pressure,
    tempf
  );

  const b_w = b_wb * Math.exp(c_w * (bubble_point_pressure - pressure));

  return b_w;
}

// pressure - P psia
// temp - T fahrenheit
// water spec grav - S_water SpG
// water vol factor corr - corr_water_vol
// returns B_w bbl/stb
export function water_phase_volume_factor(
  pressure: number,
  tempf: number,
  water_gravity: number,
  corr_water_vol: Water_Volume_Factor_Options_Enum | undefined
): number {
  if (corr_water_vol === Water_Volume_Factor_Options_Enum.Gould) {
    return (
      1 +
      0.00012 * (tempf - 60) +
      0.000001 * Math.pow(tempf - 60, 2) -
      0.00000333 * pressure
    );
  } else if (corr_water_vol === Water_Volume_Factor_Options_Enum.Jones) {
    const I6 = (water_gravity - 1) / (0.695 * Math.pow(10, -6));
    const I7 =
      -0.000003 * pressure +
      1.006 +
      ((tempf - 100) *
        (-0.000003 * pressure + 1.059 - (-0.000003 * pressure + 1.006))) /
        150;
    const I8 =
      -0.000001 * pressure +
      1.006 +
      ((tempf - 100) *
        (-0.000001 * pressure + 1.059 - (-0.000001 * pressure + 1.006))) /
        150;

    return (
      I8 +
      ((I8 - I7) *
        (0.0000005 * Math.pow(tempf, 2) - 0.000449 * tempf + 0.11395) *
        I6) /
        10000
    );
  }
  return 0;
}

// water spec grav - S_water SpG
// volume factor - B_w bbl/stb
// returns rho_w lbm/ft^3
export function water_phase_density(
  water_specific_gravity: number,
  pressure: number,
  tempf: number,
  corr_water_vol: Water_Volume_Factor_Options_Enum | undefined
): number {
  const volume_factor =
    water_phase_volume_factor(
      pressure,
      tempf,
      water_specific_gravity,
      corr_water_vol
    ) || 1;

  const density = (62.4 * water_specific_gravity) / volume_factor;
  return density;
}

// density rho_w lbm/ft^3
// returns water phase spec grav SpG
export function water_phase_specific_gravity(density: number): number {
  return density / 62.4;
}

// water prod rate - Q_w stbd
// volume factor - B_w bbl/stb
// returns q_w bbl/d
export function water_phase_production_rate_with_volume_factor(
  water_rate: number,
  volume_factor: number
): number {
  if (isNaN(volume_factor)) {
    volume_factor = 1;
  }
  return water_rate * volume_factor;
}

// water production rate Q_w_corr stbd
// volume factor B_w bbl/stb
// returns q_w_corr bbl/d
export function water_phase_production_rate_with_volume_factor_corr(
  rate: number,
  volume_factor: number
): number {
  if (isNaN(volume_factor)) {
    volume_factor = 1;
  }
  return rate * volume_factor;
}

// returns water cut percentage
// water_rate stbd
// total_liquid_flow_rate stbd
export function water_cut_percentage_from_water_rate_total_liquid_flow_rate(
  water_rate: number,
  total_liquid_flow_rate: number
): number {
  return (water_rate / total_liquid_flow_rate) * 100;
}

// returns water flow rate stbd
// total_liquid_flow_rate stbd
// water_cut percentage
export function water_flow_rate_from_total_liquid_flow_rate_water_cut_percentage(
  total_liquid_flow_rate: number,
  water_cut: number
): number {
  return total_liquid_flow_rate * PercentageUnits.Ratio.toThisUnit(water_cut);
}
